import React, { useState, useEffect } from "react";
import { adminGetAllUsers, adminGetUserById, adminGetSettings } from "../api/admin";
import { toast } from "react-toastify";
import UserDetailsView from "./UserDetailsView";
import { FaDownload, FaEye } from "react-icons/fa";
import UserReportModal from "../components/UserReportModal";
import "./UserManagement.css";

const stringToColor = (string) => {
    if (!string) return '#0095e8';
    let hash = 0;
    for (let i = 0; i < string.length; i++) {
        hash = string.charCodeAt(i) + ((hash << 5) - hash);
    }
    const colors = [
        '#0095e8', '#50cd89', '#f1416c', '#ffc700', '#7239ea',
        '#00b2ff', '#181c32', '#3f4254', '#7e8299'
    ];
    return colors[Math.abs(hash) % colors.length];
};

const UserManagement = () => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [filteredUsers, setFilteredUsers] = useState([]);
    const [selectedUserId, setSelectedUserId] = useState(null);
    const [recentUsers, setRecentUsers] = useState([]);
    const [reportModal, setReportModal] = useState({ show: false, type: 'single', data: null });
    const [siteSettings, setSiteSettings] = useState(null);

    useEffect(() => {
        fetchSiteSettings();
    }, []);

    const fetchSiteSettings = async () => {
        try {
            const res = await adminGetSettings();
            setSiteSettings(res.data);
        } catch (err) {
            console.error("Failed to load settings", err);
        }
    };

    useEffect(() => {
        fetchUsers();
    }, []);

    const fetchUsers = async () => {
        try {
            setLoading(true);
            const response = await adminGetAllUsers();
            if (response.data && response.data.success) {
                const allUsers = response.data.data;
                setUsers(allUsers);

                // Sort by date and take top 5 for "Recent" section
                const sorted = [...allUsers].sort((a, b) =>
                    new Date(b.createdAt) - new Date(a.createdAt)
                ).slice(0, 5);
                setRecentUsers(sorted);
            } else {
                toast.error("Failed to fetch users");
            }
        } catch (error) {
            console.error("Error fetching users:", error);
            toast.error("Error fetching users");
        } finally {
            setLoading(false);
        }
    };

    const handleDownloadUserReport = async (userId, userName) => {
        try {
            toast.info(`Preparing report for ${userName}...`);
            const userRes = await adminGetUserById(userId);
            if (userRes.data && userRes.data.success) {
                setReportModal({ show: true, type: 'single', data: userRes.data });
            } else {
                toast.error("Failed to fetch user details for report");
            }
        } catch (error) {
            console.error("Report generation error:", error);
            toast.error("Failed to generate user report");
        }
    };

    const handleDownloadAllUsers = async () => {
        if (users.length === 0) {
            toast.warning("No users available to export");
            return;
        }

        setReportModal({ show: true, type: 'all', data: users });
    };

    useEffect(() => {
        const filtered = users.filter(user => {
            const term = searchTerm.toLowerCase();
            const matchesSearch = (
                (user.name?.toLowerCase() || "").includes(term) ||
                (user.email?.toLowerCase() || "").includes(term) ||
                (user.id?.toLowerCase() || user._id?.toLowerCase() || "").includes(term) ||
                (user.mobile?.toString() || "").includes(term)
            );

            const matchesStatus =
                statusFilter === "all" ||
                (statusFilter === "verified" && user.isVerified) ||
                (statusFilter === "unverified" && !user.isVerified);

            return matchesSearch && matchesStatus;
        });
        setFilteredUsers(filtered);
    }, [searchTerm, statusFilter, users]);

    // Scroll to top when switching between views
    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, [selectedUserId]);

    return (
        <div className="user-management-container">
            {!selectedUserId ? (
                <>
                    <div className="page-header">
                        <h2 className="adminproducts">Users List</h2>
                        <div className="header-actions">
                            <div className="search-container">
                                <span className="search-label">Search:</span>
                                <input
                                    type="text"
                                    className="user-search-input"
                                    placeholder="Search by name, ID, contact..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </div>
                            <div className="filter-container">
                                <span className="filter-label">Status:</span>
                                <select 
                                    className="status-select"
                                    value={statusFilter}
                                    onChange={(e) => setStatusFilter(e.target.value)}
                                >
                                    <option value="all">All Users</option>
                                    <option value="verified">Active</option>
                                    <option value="unverified">Inactive</option>
                                </select>
                            </div>
                            <div className="export-container">
                                <button 
                                    className="export-btn" 
                                    onClick={handleDownloadAllUsers}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                        padding: '8px 16px',
                                        backgroundColor: '#0095e8',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '0',
                                        cursor: 'pointer',
                                        fontWeight: '600',
                                        fontSize: '0.9rem'
                                    }}
                                >
                                    <FaDownload /> NBitez User List
                                </button>
                            </div>
                            <div className="admin-count-badge">
                                Total Users: {users.length}
                            </div>
                        </div>
                    </div>

                    {recentUsers.length > 0 && (
                        <div className="recent-users-section">
                            <h3 className="section-title">New Registrations</h3>
                            <div className="recent-users-grid">
                                {recentUsers.map(user => (
                                    <div key={user.id || user._id} className="user-card" onClick={() => setSelectedUserId(user.id || user._id)}>
                                        <div className="user-avatar-mini">
                                            {user.name?.[0]?.toUpperCase() || "U"}
                                        </div>
                                        <div className="user-card-info">
                                            <span className="user-card-name">{user.name}</span>
                                            <span className="user-card-date">{new Date(user.createdAt).toLocaleDateString()}</span>
                                        </div>
                                        <div className={`user-card-status ${user.isVerified ? 'verified' : 'unverified'}`}></div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="users-table-wrapper">
                        {loading ? (
                            <div style={{ textAlign: 'center', padding: '40px', color: '#7e8299' }}>Loading users...</div>
                        ) : (
                            <table className="users-table">
                                <thead>
                                    <tr>
                                        <th>Name</th>
                                        <th>Email</th>
                                        <th>Mobile</th>
                                        <th style={{ textAlign: 'center' }}>Role</th>
                                        <th>Reg. Date</th>
                                        <th>Status</th>
                                        <th style={{ textAlign: 'center' }}>Wallet</th>
                                        <th style={{ textAlign: 'center' }}>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredUsers.length > 0 ? (
                                        filteredUsers.map((user, index) => (
                                            <tr key={user.id || user._id}>
                                                <td data-label="NAME">
                                                    <div className="user-info-cell">
                                                        <div className="user-avatar-mini" style={{ backgroundColor: stringToColor(user.name) }}>
                                                            {user.name?.charAt(0).toUpperCase()}
                                                        </div>
                                                        <div className="user-name-wrapper">
                                                            <div className="user-name-main">{user.name || "N/A"}</div>
                                                            <div className="user-id-sub">#{String(user.id || user._id).slice(-6).toUpperCase()}</div>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td data-label="EMAIL">{user.email || "-"}</td>
                                                <td data-label="MOBILE">{user.mobile || "-"}</td>
                                                <td data-label="ROLE">
                                                    <span className="role-badge">{user.role || "User"}</span>
                                                </td>
                                                <td data-label="JOIN DATE">{user.createdAt ? new Date(user.createdAt).toLocaleDateString('en-GB') : "N/A"}</td>
                                                <td data-label="STATUS">
                                                    <span className={`status-badge ${user.isVerified !== false ? 'active' : 'inactive'}`}>
                                                        {user.isVerified !== false ? 'ACTIVE' : 'INACTIVE'}
                                                    </span>
                                                </td>
                                                <td data-label="WALLET" className="font-bold-text">₹{(user.walletBalance || 0).toLocaleString()}</td>
                                                <td data-label="ACTIONS">
                                                    <div className="action-buttons">
                                                        <button
                                                            className="action-btn view"
                                                            onClick={() => setSelectedUserId(user.id || user._id)}
                                                            title="View Details"
                                                        >
                                                            <FaEye /> <span className="btn-text-mobile">View</span>
                                                        </button>
                                                        <button
                                                            className="action-btn download"
                                                            onClick={() => handleDownloadUserReport(user.id || user._id, user.name)}
                                                            title="Download Report"
                                                        >
                                                            <FaDownload /> <span className="btn-text-mobile">Download</span>
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="7" className="empty-table-cell">
                                                <div className="no-data-alert">
                                                    <span className="no-data-icon">👥</span>
                                                    <p>No users found on the server.</p>
                                                    <small>Wait for backend user list API integration.</small>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        )}
                    </div>
                </>
            ) : (
                <>
                    <div className="page-header">
                        <div className="header-with-back">
                            <button className="back-btn" onClick={() => setSelectedUserId(null)}>← Back to Users</button>
                            <h2 className="adminproducts">User Information</h2>
                        </div>
                    </div>
                    <UserDetailsView userId={selectedUserId} />
                </>
            )}

            {reportModal.show && (
                <UserReportModal
                    type={reportModal.type}
                    data={reportModal.data}
                    siteSettings={siteSettings}
                    onClose={() => setReportModal({ ...reportModal, show: false })}
                />
            )}
        </div>
    );
};

export default UserManagement;

